import * as d3 from 'd3';
import React from 'react';
import ReactDOM from 'react-dom';

import {Assembly, AssemblySlotType} from 'assembly/AssemblyModels';
import SlotTemplate, {SlotProps} from 'assembly/SlotTemplate';
import AssemblyErrorsWarnings from 'assembly/views/AssemblyErrorsWarnings';
import SingleFragmentSlotView, {SingleFragmentSlotViewOptions} from 'assembly/views/SingleFragmentSlotView';
import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import * as util from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import * as AssemblyConstants from 'constants/AssemblyConstants';
import CutSites from 'molbio/enzymes/backbone/CutSites';
import EnzymeActivityPopoverLink from 'molbio/enzymes/EnzymeActivityPopoverLink';
import {Provider} from 'molbio/enzymes/EnzymeConstants';
import * as EnzymeUtil from 'molbio/enzymes/EnzymeUtil';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as logging from 'util/Logging';

const {ASSEMBLY_TYPE} = AssemblyConstants;

interface RestrictionSlotDatum {
  right: [string, string] | null;
  left: [string, string] | null;
  x1: number;
  x2: number;
  matched?: boolean;
  width: number;
  dx?: number;
}

interface JunctionSide {
  fragment?: BioJSSequence;
  side: 'right' | 'left';
  className: string;
  matched?: boolean;
}

class RestrictionSlotView extends SingleFragmentSlotView {
  assembly!: Assembly;
  cutsites?: CutSites;
  template!: (props: SlotProps) => string;

  static override initClass(): void {
    this.prototype.template = SlotTemplate;
  }

  override events(): Backbone.EventsHash {
    return {
      ...super.events(),
      'click .js-done': 'done',
      'click .js-reset': 'reset',
      'click .set': 'set',
      'click .js-jump': 'jump',
      'click .js-flip': 'flip',
    };
  }

  override initialize(options: SingleFragmentSlotViewOptions): void {
    this.setProvider = this.setProvider.bind(this);
    super.initialize(options);
    this.assembly = options.assembly;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model, 'change:errors change:warnings', this.updateWarnings);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.fragments(), 'add remove change', this.updatePreview);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.assembly, 'change:provider', this.updateBuffers);
  }

  done(): void {
    this.model.set({active: false});
  }

  jump(): void {
    const fragment = this.model.singleFragment();
    this.fileStates.selectFragment(fragment.getSequence()!, this.model.get('sequenceFragment')!);
  }

  flip(): void {
    this.model.setSingleFragment({reverse: !this.model.singleFragment().reverse()});
  }

  reset(): void {
    this.model.singleFragment().destroy();
  }

  shouldSetFromSelectionBeDisabled(): boolean {
    const selected = this.cutsites != null ? this.cutsites.where({selected: true}) : [];
    const numCuts = this.selection != null && this.selection.start() === this.selection.end() ? 1 : 2;
    return this.selection == null || !this.selection.canDigest() || selected.length < numCuts;
  }

  set(): void {
    if (this.shouldSetFromSelectionBeDisabled()) {
      return;
    }
    const startCut = this.selection!.getStartState('cutsite');
    const endCut = this.selection!.getEndState('cutsite');
    if (!(startCut != null && endCut != null)) {
      notifications.warn('Please recreate your selection by shift-selecting cut sites.');
      logging.warn('Restriction assembly wizard had invalid selection.');
      this.selection!.reset(this.selection!.start());
      return;
    }

    const startEnzyme = this.selection!.getStartState('cutsite')!.get('enzyme');
    const endEnzyme = this.selection!.getEndState('cutsite')!.get('enzyme');
    startEnzyme.set({active: true});
    endEnzyme.set({active: true});

    const fragmentStart = this.selection!.start();
    // Important for matching up with digest fragments, whose end is 0 if it goes to end of sequence.
    const fragmentEnd = sequtil.mod(this.selection!.end()!, this.sequence!.length());

    this.model
      .setSingleFragment({
        sequenceID: this.sequence!.id,
        // @ts-expect-error missing id and folder
        sequenceMetadata: {
          name: this.sequence!.name(),
        },
        name: this.sequence!.name(),
        start: fragmentStart,
        end: fragmentEnd,
        startEnzymeID: startEnzyme.id,
        endEnzymeID: endEnzyme.id,
        reverse: false,
      })
      .fail(notifications.notifyError);
  }

  updatePreview(): void {
    let left;
    let matched;
    let right;
    let x2;
    const junctions: [[JunctionSide, JunctionSide], [JunctionSide, JunctionSide]] = [
      [
        {
          fragment: this.model.get('prevFragment'),
          side: 'right',
          className: '.prev-preview',
          matched: this.model.get('prevMatched'),
        },
        {
          fragment: this.model.get('thisFragment'),
          side: 'left',
          className: '.this-left-preview',
          matched: this.model.get('prevMatched'),
        },
      ],
      [
        {
          fragment: this.model.get('thisFragment'),
          side: 'right',
          className: '.this-right-preview',
          matched: this.model.get('nextMatched'),
        },
        {
          fragment: this.model.get('nextFragment'),
          side: 'left',
          className: '.next-preview',
          matched: this.model.get('nextMatched'),
        },
      ],
    ];

    const data: Array<RestrictionSlotDatum> = [];
    let totalWidth = 0;

    function getBases(model: JunctionSide): [string, string] | null {
      let bottom;
      let offset;
      let top;
      if (model.fragment == null) {
        return null;
      }
      const bases = model.fragment.bases();
      const {length} = bases;
      const extraBases = 3;
      const spaces = (n: number): string =>
        __range__(0, n, false)
          .map(() => ' ')
          .join('');
      if (model.side === 'right') {
        offset = model.fragment.get('offset3');
        top = bases.slice(length - extraBases - Math.abs(offset));
        bottom = util.complement(top, DNA_COMPLEMENT_TABLE);
        if (offset <= 0) {
          bottom = bottom.slice(0, extraBases) + spaces(Math.abs(offset));
        } else {
          top = top.slice(0, extraBases) + spaces(offset);
        }
      } else {
        offset = model.fragment.get('offset5');
        top = bases.slice(0, extraBases + Math.abs(offset));
        bottom = util.complement(top, DNA_COMPLEMENT_TABLE);
        if (offset >= 0) {
          bottom = spaces(offset) + bottom.slice(offset);
        } else {
          top = spaces(offset) + top.slice(Math.abs(offset));
        }
      }
      return [top, bottom];
    }

    for (let index = 0; index < junctions.length; index++) {
      let width: number;
      const junction = junctions[index];
      [right, left] = Array.from(junction);
      const rightBases = getBases(right);
      const leftBases = getBases(left);
      ({matched} = right);
      const x1 = 0;
      x2 = 0;

      if (rightBases != null && leftBases != null) {
        const rightOffset3 = right.fragment!.get('offset3');
        const leftOffset5 = left.fragment!.get('offset5');

        if (rightOffset3 > 0 && leftOffset5 > 0) {
          x2 = rightBases[0].trim().length;
          if (rightOffset3 > leftOffset5) {
            x2 += rightOffset3 - leftOffset5;
          }
        } else if (rightOffset3 < 0 && leftOffset5 < 0) {
          x2 = rightBases[1].trim().length;
          if (rightOffset3 < leftOffset5) {
            x2 -= rightOffset3 + leftOffset5;
          }
        } else {
          x2 = Math.max(rightBases[0].trim().length, rightBases[1].trim().length);
        }
      }

      if (rightBases != null && leftBases != null) {
        width = x2 + Math.max(leftBases[1].trim().length, leftBases[0].trim().length);
      } else if (rightBases != null) {
        width = Math.max(rightBases[0].length, rightBases[1].length);
      } else if (leftBases != null) {
        width = Math.max(leftBases[0].length, leftBases[1].length);
      } else {
        width = 0;
      }

      const datum: RestrictionSlotDatum = {
        right: rightBases,
        left: leftBases,
        x1,
        x2,
        matched,
        width,
      };

      totalWidth += width;
      data.push(datum);
    }

    if (data.length > 0) {
      data[0].dx = 0;
      data[1].dx = data[0].width;
    }

    this.$('.preview').empty();

    const COL_WIDTH = 392;
    const FONT_WIDTH = 11;
    let FONT_HEIGHT = 23;
    FONT_HEIGHT = 22;
    const BUFFER_WIDTH = 2 * FONT_WIDTH;
    const VERTICAL_PADDING = 15;

    const translateX = (COL_WIDTH - totalWidth * FONT_WIDTH - BUFFER_WIDTH) / 2;
    let root = d3
      .select<HTMLLIElement, RestrictionSlotDatum>(this.el)
      .select<HTMLDivElement>('div.preview')
      .append<SVGElement>('svg:svg')
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .append<SVGGElement>('svg:g')
      .attr('transform', `translate(${translateX}, ${VERTICAL_PADDING})`)
      .classed('mono', true);

    // @ts-expect-error d3 does not like that we are going from the outer HTMLElement to the inner SVGGElement
    // in two separate operations
    root = root.selectAll<SVGGElement, RestrictionSlotDatum>('g').data(data);

    root
      .enter()
      .append('svg:g')
      .attr('transform', (d: RestrictionSlotDatum, i) => {
        if (d.dx! > 0) {
          return `translate(${d.dx! * FONT_WIDTH + BUFFER_WIDTH})`;
        } else {
          return 'translate(0,0)';
        }
      })
      .each(function (d, i) {
        if (d.right != null) {
          right = d3.select(this).append('svg:g');
          right = right
            .append('svg:text')
            .classed('side', i === 0)
            .classed('mismatch', !d.matched && i === 1);
          right.append('svg:tspan').attr('xml:space', 'preserve').text(d.right[0]);
          right
            .append('svg:tspan')
            .attr('xml:space', 'preserve')
            .text(d.right[1])
            .attr('x', 0)
            .attr('y', FONT_HEIGHT);
        }
        if (d.left != null) {
          left = d3
            .select(this)
            .append('svg:g')
            .attr('transform', `translate(${FONT_WIDTH * d.x2})`);
          left = left
            .append('svg:text')
            .classed('side', i === 1)
            .classed('mismatch', !d.matched && i === 0);
          left.append('svg:tspan').attr('xml:space', 'preserve').text(d.left[0]);
          left
            .append('svg:tspan')
            .attr('xml:space', 'preserve')
            .text(d.left[1])
            .attr('x', 0)
            .attr('dy', FONT_HEIGHT);
        }
      });

    root.exit().remove();
  }

  updateWarnings(): void {
    const container = this.$('.assembly-check-mount');
    if (container.length === 0) {
      return;
    }
    const component = (
      <AssemblyErrorsWarnings
        warnings={this.model.warnings()}
        errors={this.model.errors()}
        method={ASSEMBLY_TYPE.DIGEST}
      />
    );
    renderToDOM(component, container[0]);
  }

  setProvider(provider: Provider): void {
    this.assembly.set({provider: provider.name});
  }

  updateBuffers(): void {
    if (this.model.isEmpty() || !this.model.active()) {
      // SlotTemplate does not render .buffers in the empty or in-active case.
      return;
    }
    const fragment = this.model.singleFragment();
    const enzymes = fragment.getSequence()!.get('enzymes');
    const enzymeIds = fragment.enzymeIDs()!;

    const providers = EnzymeUtil.getProvidersForEnzymes(enzymeIds);
    const provider = EnzymeUtil.getPreferredProviderForEnzyme(enzymeIds, this.assembly.get('provider'));

    renderToDOM(
      <EnzymeActivityPopoverLink
        provider={provider}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onChangeProvider={this.setProvider}
        providers={providers}
        enzymes={enzymes.getEnzymesJSON(enzymeIds)}
      />,
      this.$('.buffers')[0]
    );
  }

  override updateSequence(sequence: Sequence): void {
    super.updateSequence(sequence);
    if (this.cutsites != null) {
      this.stopListening(this.cutsites);
    }
    this.cutsites = sequence != null ? sequence.get('cutsites') : undefined;
    if (this.cutsites != null) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(this.cutsites, 'change:selected', this.updateToolbar);
    }
    this.updateToolbar();
  }

  updateToolbar(): void {
    this.$('.set').toggleClass('is-disabled', this.shouldSetFromSelectionBeDisabled());
    // Disable interaction if the cuts is invalid (so no fragment).
    this.$('.js-jump, .js-flip').toggleClass('hidden', this.model.get('thisFragment') == null);
    this.$('[data-toggle="tooltip"]').tooltip({container: 'body'});
  }

  cleanUpOverlays(): void {
    this.$('[data-toggle="tooltip"]').tooltip('destroy');
    // Unmount the EnzymeActivityPopoverLink to clean up its popover
    if (this.$('.buffers')[0] != null) {
      ReactDOM.unmountComponentAtNode(this.$('.buffers')[0]);
    }
  }

  override remove(): Backbone.View<AssemblySlotType> {
    this.cleanUpOverlays();
    return super.remove();
  }

  override render(): Backbone.View<AssemblySlotType> {
    this.cleanUpOverlays();

    const fragment = this.model.isEmpty() ? null : this.model.singleFragment();

    let info: SlotProps;

    if (this.model.isEmpty()) {
      info = {
        active: this.model.active(),
        empty: true,
        slotName: this.model.displayName()!,
      };
    } else {
      info = {
        active: this.model.active(),
        empty: false,
        enzymes: fragment!.getEnzymes()!,
        length: fragment!.length(),
        name: fragment!.sequenceMetadata().name,
        reverse: fragment!.reverse(),
        slotName: this.model.displayName()!,
      };
    }

    this.$el.html(this.template(info));
    this.$el.toggleClass('active', this.model.active());
    this.updateToolbar();
    this.updateWarnings();
    this.updatePreview();
    this.updateBuffers();
    this.toggleDelete();
    return this;
  }
}
RestrictionSlotView.initClass();

export default RestrictionSlotView;

function __range__(left: number, right: number, inclusive: boolean): Array<number> {
  const range = [];
  const ascending = left < right;
  const end = !inclusive ? right : ascending ? right + 1 : right - 1;
  for (let i = left; ascending ? i < end : i > end; ascending ? i++ : i--) {
    range.push(i);
  }
  return range;
}
